/**
 * RoomOrientationDebugWidget - Debug controls for room orientation detection visualization
 */

import { DebugControlsWidget } from './DebugControlsWidget';
import { toggleDebugGroupVisibility, IViewerScene } from './DebugVisualization';

/** Callback for when debug visualization state changes */
export type DebugStateChangeCallback = (anyEnabled: boolean) => void;

export class RoomOrientationDebugWidget extends DebugControlsWidget {
  static readonly WIDGET_NAME = 'room-orientation-debug';
  private viewer: IViewerScene;

  /** Track which debug visualizations are enabled */
  private enabledStates: Record<string, boolean> = {
    rays: false,
    'raw-points': false,
    'filtered-points': false,
    'wall-points': false,
  };

  /** Callback when any debug visualization is enabled/disabled */
  private onDebugStateChange: DebugStateChangeCallback | null = null;

  constructor(viewer: IViewerScene) {
    super(RoomOrientationDebugWidget.WIDGET_NAME);
    this.viewer = viewer;
    this.setupControls();
  }

  /**
   * Set callback for when debug visualization state changes
   */
  setDebugStateChangeCallback(callback: DebugStateChangeCallback | null): void {
    this.onDebugStateChange = callback;
  }

  /**
   * Check if any debug visualization is enabled
   */
  isAnyDebugEnabled(): boolean {
    return Object.values(this.enabledStates).some((enabled) => enabled);
  }

  private updateDebugState(key: string, enabled: boolean): void {
    const wasAnyEnabled = this.isAnyDebugEnabled();
    this.enabledStates[key] = enabled;
    const isAnyEnabled = this.isAnyDebugEnabled();

    // Notify if the overall state changed
    if (wasAnyEnabled !== isAnyEnabled && this.onDebugStateChange) {
      this.onDebugStateChange(isAnyEnabled);
    }
  }

  private setupControls(): void {
    // Rays toggle - shows all rays cast during detection
    this.addControl('rays', {
      type: 'checkbox',
      label: 'Rays (green=hit, red=miss)',
      checked: false,
      onChange: (checked) => {
        toggleDebugGroupVisibility(this.viewer, 'room-orientation-rays-debug', checked);
        this.updateDebugState('rays', checked);
      },
    });

    // Raw points toggle - shows all points found along rays before filtering
    this.addControl('raw-points', {
      type: 'checkbox',
      label: 'Raw Points (red)',
      checked: false,
      onChange: (checked) => {
        toggleDebugGroupVisibility(this.viewer, 'room-orientation-raw-points-debug', checked);
        this.updateDebugState('raw-points', checked);
      },
    });

    // Filtered points toggle - shows points after proximity clustering
    this.addControl('filtered-points', {
      type: 'checkbox',
      label: 'Filtered Points (yellow)',
      checked: false,
      onChange: (checked) => {
        toggleDebugGroupVisibility(this.viewer, 'room-orientation-filtered-points-debug', checked);
        this.updateDebugState('filtered-points', checked);
      },
    });

    // Wall points toggle - shows points that were identified as wall inliers
    this.addControl('wall-points', {
      type: 'checkbox',
      label: 'Wall Inliers (green)',
      checked: false,
      onChange: (checked) => {
        toggleDebugGroupVisibility(this.viewer, 'room-orientation-wall-points-debug', checked);
        this.updateDebugState('wall-points', checked);
      },
    });
  }

  /**
   * Clear all debug visualizations from the scene
   */
  clearDebugVisualizations(): void {
    toggleDebugGroupVisibility(this.viewer, 'room-orientation-rays-debug', false);
    toggleDebugGroupVisibility(this.viewer, 'room-orientation-raw-points-debug', false);
    toggleDebugGroupVisibility(this.viewer, 'room-orientation-filtered-points-debug', false);
    toggleDebugGroupVisibility(this.viewer, 'room-orientation-wall-points-debug', false);
  }
}
